import { useCallback, useMemo } from "react";
import { actions, ensurePluginOrder, makePropGetter, useGetLatest, useMountedLayoutEffect } from "react-table";

const pluginName = "useRowSelect";

// Actions
actions.resetSelectedRows = "resetSelectedRows";
actions.toggleAllRowsSelected = "toggleAllRowsSelected";
actions.toggleRowSelected = "toggleRowSelected";
actions.toggleAllPageRowsSelected = "toggleAllPageRowsSelected";
actions.calculateAllPageRowsSelectedProps = "calculateAllPageRowsSelectedProps";

export const useRowSelect = hooks => {
  hooks.getToggleRowSelectedProps = [ defaultGetToggleRowSelectedProps ];
  hooks.getToggleAllRowsSelectedProps = [ defaultGetToggleAllRowsSelectedProps ];
  hooks.getToggleAllPageRowsSelectedProps = [ defaultGetToggleAllPageRowsSelectedProps ];
  hooks.stateReducers.push(reducer);
  hooks.useInstance.push(useInstance);
  hooks.prepareRow.push(prepareRow);
};

useRowSelect.pluginName = pluginName;

const defaultGetToggleRowSelectedProps = (props, {
  instance,
  row,
}) => {
  const { manualRowSelectedKey = "isSelected" } = instance;
  let checked = false;

  if (row.original && row.original[manualRowSelectedKey]) {
    checked = true;
  } else {
    checked = row.isSelected;
  }

  return [
    props,
    {
      onChange: () => row.toggleRowSelected(!checked),
      value: checked,
      indeterminate: row.isSomeSelected,
    },
  ];
};

const defaultGetToggleAllRowsSelectedProps = (props, { instance }) => [
  props,
  {
    onChange: (e, value) => {
      instance.toggleAllRowsSelected(value);
    },
    value: instance.state.selectedRowIds?.selectedAll,
    indeterminate: instance.state.selectedRowIds?.indeterminate,
  },
];

const defaultGetToggleAllPageRowsSelectedProps = (props, { instance }) => [
  props,
  {
    onChange: (e, value) => {
      instance.toggleAllPageRowsSelected(value);
    },
    value: instance.state.selectedRowIds?.selectedAllPage,
    indeterminate: instance.state.selectedRowIds?.indeterminatePage,
  },
];

const defaultSelectedRowIds = {
  selectedAll: false,
  indeterminate: false,
  selectedAllPage: false,
  indeterminatePage: false,
  selected: {},
  unselected: {},
  count: 0,
};

// eslint-disable-next-line max-params
function reducer(state, action, previousState, instance) {
  if (!instance.allowRowSelect) {
    return state;
  }

  const getToggleAllPageRowsSelectedProps = (selectedRowIds, getSubRows) => {
    let selectedAllPage = null;
    let someSelected = false;
    instance.page.forEach(row => {
      const isRowSelected = getRowIsSelected(row, selectedRowIds, getSubRows);
      if (isRowSelected) {
        someSelected = true;
        if (selectedAllPage === null) {
          selectedAllPage = true;
        }
      } else {
        selectedAllPage = false;
      }
    });
    return {
      selectedAllPage: selectedAllPage,
      indeterminatePage: !selectedAllPage && someSelected,
    };
  };

  const setRowSelection = (id, setSelected, selectedRowIds) => {
    const {
      rowsById,
      selectSubRows = true,
      getSubRows,
    } = instance;

    const isSelected =
      (selectedRowIds.selectedAll && !selectedRowIds.unselected[id]) ||
      (!selectedRowIds.selectedAll && selectedRowIds.selected[id]);
    const shouldExist = typeof setSelected !== "undefined" ? setSelected : !isSelected;

    if (isSelected === shouldExist) {
      return selectedRowIds;
    }

    const newSelectedRowIds = {
      ...selectedRowIds,
      selected: {
        ...selectedRowIds.selected,
      },
      unselected: {
        ...selectedRowIds.unselected,
      },
    };

    const handleRowById = id => {
      const row = rowsById[id];

      if (!row.isGrouped) {
        if (shouldExist) {
          if (newSelectedRowIds.selectedAll) {
            delete newSelectedRowIds.unselected[id];
          } else {
            newSelectedRowIds.selected[id] = true;
          }
        } else {
          if (newSelectedRowIds.selectedAll) {
            newSelectedRowIds.unselected[id] = true;
          } else {
            delete newSelectedRowIds.selected[id];
          }
        }
      }

      if (selectSubRows && getSubRows(row)) {
        return getSubRows(row)
          .forEach(row => handleRowById(row.id));
      }
    };

    handleRowById(id);

    Object.assign(newSelectedRowIds, getToggleAllPageRowsSelectedProps(newSelectedRowIds, selectSubRows ? getSubRows : undefined));

    // if (Object.keys(newSelectedRowIds.unselected).length === instance.totalCount) {
    //   newSelectedRowIds.selectedAll = false;
    //   newSelectedRowIds.unselected = {};
    // }
    // if (Object.keys(newSelectedRowIds.selected).length === instance.totalCount) {
    //   newSelectedRowIds.selectedAll = !!instance.totalCount;
    //   newSelectedRowIds.selected = {};
    // }

    newSelectedRowIds.indeterminate =
      !!(
        (newSelectedRowIds.selectedAll && Object.keys(newSelectedRowIds.unselected).length) ||
        (!newSelectedRowIds.selectedAll && Object.keys(newSelectedRowIds.selected).length)
      );

    if (newSelectedRowIds.selectedAll) {
      newSelectedRowIds.count = instance.totalCount - Object.keys(newSelectedRowIds.unselected).length;
    } else {
      newSelectedRowIds.count = Object.keys(newSelectedRowIds.selected).length;
    }

    return newSelectedRowIds;
  };


  if (action.type === actions.init) {
    return {
      selectedRowIds: { ...defaultSelectedRowIds },
      totalCount: instance.totalCount,
      ...state,
    };
  }

  if (
    action.type === actions.resetSelectedRows ||
    (action.type === "setFilter" && state.selectedRowIds.selectedAll)
  ) {
    return {
      ...state,
      selectedRowIds: instance.initialState.selectedRowIds || {
        ...defaultSelectedRowIds,
      },
      totalCount: instance.totalCount,
    };
  }

  if (action.type === actions.calculateAllPageRowsSelectedProps) {
    const {
      selectSubRows = true,
      getSubRows,
    } = instance;
    const {
      selectedAllPage,
      indeterminatePage,
    } = getToggleAllPageRowsSelectedProps(state.selectedRowIds, selectSubRows ? getSubRows : undefined);

    if (selectedAllPage !== state.selectedRowIds?.selectedAllPage || indeterminatePage !== state.selectedRowIds?.indeterminatePage) {
      return {
        ...state,
        selectedRowIds: {
          ...state.selectedRowIds,
          selectedAllPage,
          indeterminatePage,
        },
        totalCount: instance.totalCount,
      };
    }
    return state;
  }

  if (action.type === actions.toggleAllRowsSelected) {
    return {
      ...state,
      selectedRowIds: {
        ...defaultSelectedRowIds,
        selectedAll: action.value,
        selectedAllPage: action.value,
        count: action.value ? instance.totalCount : 0,
      },
      totalCount: instance.totalCount,
    };
  }

  if (action.type === actions.toggleRowSelected) {
    const {
      id,
      value: setSelected,
    } = action;
    return {
      ...state,
      selectedRowIds: setRowSelection(id, setSelected, state.selectedRowIds),
      totalCount: instance.totalCount,
    };
  }

  if (action.type === actions.toggleAllPageRowsSelected) {
    const { value: setSelected } = action;
    return {
      ...state,
      selectedRowIds: instance.page.reduce((selectedRowIds, row) => setRowSelection(row.id, setSelected, selectedRowIds), state.selectedRowIds),
      totalCount: instance.totalCount,
    };
  }

  return state;
}

function useInstance(instance) {
  const {
    data,
    rows,
    getHooks,
    plugins,
    autoResetSelectedRows = true,
    state: { selectedRowIds },
    selectSubRows = true,
    dispatch,
    getSubRows,
  } = instance;

  ensurePluginOrder(
    plugins,
    [ "useFilters", "useGroupBy", "useSortBy", "useExpanded", "usePagination" ],
    "useRowSelect",
  );

  const selectedFlatRows = useMemo(() => {
    const selectedFlatRows = [];

    rows.forEach(row => {
      const isSelected = getRowIsSelected(row, selectedRowIds, selectSubRows ? getSubRows : undefined);
      row.isSelected = !!isSelected;
      row.isSomeSelected = isSelected === null;

      if (isSelected) {
        selectedFlatRows.push(row);
      }
    });

    return selectedFlatRows;
  }, [ rows, selectSubRows, selectedRowIds, getSubRows ]);

  const getAutoResetSelectedRows = useGetLatest(autoResetSelectedRows);

  useMountedLayoutEffect(() => {
    dispatch({ type: actions.calculateAllPageRowsSelectedProps });

    if (getAutoResetSelectedRows()) {
      dispatch({ type: actions.resetSelectedRows });
    }
  }, [ dispatch, data ]);

  const toggleAllRowsSelected = useCallback(
    value => dispatch({
      type: actions.toggleAllRowsSelected,
      value,
    }),
    [ dispatch ],
  );

  const toggleAllPageRowsSelected = useCallback(
    value => dispatch({
      type: actions.toggleAllPageRowsSelected,
      value,
    }),
    [ dispatch ],
  );

  const toggleRowSelected = useCallback(
    (id, value) => dispatch({
      type: actions.toggleRowSelected,
      id,
      value,
    }),
    [ dispatch ],
  );

  const getInstance = useGetLatest(instance);

  const getToggleAllRowsSelectedProps = makePropGetter(
    getHooks().getToggleAllRowsSelectedProps,
    { instance: getInstance() },
  );

  const getToggleAllPageRowsSelectedProps = makePropGetter(
    getHooks().getToggleAllPageRowsSelectedProps,
    { instance: getInstance() },
  );

  Object.assign(instance, {
    selectedFlatRows,
    toggleRowSelected,
    toggleAllRowsSelected,
    getToggleAllRowsSelectedProps,
    getToggleAllPageRowsSelectedProps,
    toggleAllPageRowsSelected,
  });
}

function prepareRow(row, { instance }) {
  if (instance.allowRowSelect) {
    row.toggleRowSelected = set => instance.toggleRowSelected(row.id, set);

    row.getToggleRowSelectedProps = makePropGetter(
      instance.getHooks().getToggleRowSelectedProps,
      {
        instance: instance,
        row,
      },
    );
  }
}

function getRowIsSelected(row, selectedRowIds, getSubRows) {
  const subRows = getSubRows?.(row);

  if (!subRows?.length) {
    if (
      (selectedRowIds?.selectedAll && !selectedRowIds?.unselected?.[row.id]) ||
      (!selectedRowIds?.selectedAll && selectedRowIds?.selected?.[row.id])
    ) {
      return true;
    }
  } else {
    let allChildrenSelected = true;
    let someSelected = false;

    subRows.forEach(subRow => {
      // Bail out early if we know both of these
      if (someSelected && !allChildrenSelected) {
        return;
      }

      if (getRowIsSelected(subRow, selectedRowIds, getSubRows)) {
        someSelected = true;
      } else {
        allChildrenSelected = false;
      }
    });
    return allChildrenSelected ? true : someSelected ? null : false;
  }

  return false;
}
